import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Apple, Search, Plus, Check, Star, X, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const NutritionTracker = ({ onComplete, promptId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [todayMeals, setTodayMeals] = useState({
    breakfast: { foods: [], feeling: '', completed: false },
    morning_snack: { foods: [], feeling: '', completed: false },
    lunch: { foods: [], feeling: '', completed: false },
    afternoon_snack: { foods: [], feeling: '', completed: false },
    dinner: { foods: [], feeling: '', completed: false },
    evening_snack: { foods: [], feeling: '', completed: false }
  });
  const [currentMeal, setCurrentMeal] = useState('breakfast');
  const [feeling, setFeeling] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);

  const mealLabels = {
    breakfast: '🌅 Breakfast',
    morning_snack: '🍎 Morning Snack',
    lunch: '🌞 Lunch',
    afternoon_snack: '🥤 Afternoon Snack',
    dinner: '🌙 Dinner',
    evening_snack: '🍪 Evening Snack'
  };

  useEffect(() => {
    loadTodayMeals();
    loadFavorites();
    loadWeeklySummary();
  }, []);

  const loadTodayMeals = async () => {
    try {
      const response = await axios.get(`${API}/api/nutrition/today`);
      setTodayMeals(response.data.meals || todayMeals);
    } catch (error) {
      console.error('Failed to load nutrition data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/api/nutrition/favorites`);
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const loadWeeklySummary = async () => {
    try {
      const response = await axios.get(`${API}/api/nutrition/weekly-summary`);
      setWeeklySummary(response.data);
    } catch (error) {
      console.error('Failed to load weekly summary:', error);
    }
  };

  const searchFood = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a food name');
      return;
    }

    setSearching(true);
    setShowFavorites(false);
    try {
      const response = await axios.get(`${API}/api/nutrition/search`, {
        params: { query: searchQuery }
      });
      setSearchResults(response.data.foods || []);
      
      if (response.data.foods.length === 0) {
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search foods');
    } finally {
      setSearching(false);
    }
  };

  const requestCustomFood = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a food name');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/nutrition/add-custom-food`, null, {
        params: { food_name: searchQuery.trim() }
      });
      
      if (response.data.already_exists) {
        toast.info(response.data.message);
        if (response.data.message.includes('database')) {
          searchFood();
        }
      } else {
        toast.success(response.data.message);
        const addedFoodName = searchQuery.trim();
        setSearchQuery('');
        setTimeout(() => {
          setSearchQuery(addedFoodName);
          searchFood();
        }, 500);
      }
    } catch (error) {
      console.error('Add custom food error:', error);
      toast.error('Failed to add custom food');
    }
  };

  const addFoodToMeal = (food) => {
    const foodId = `${food.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setTodayMeals(prev => ({
      ...prev,
      [currentMeal]: {
        ...prev[currentMeal],
        foods: [
          ...prev[currentMeal].foods,
          {
            id: foodId,
            name: food.name,
            calories: food.calories,
            protein: food.protein,
            carbs: food.carbs,
            fat: food.fat
          }
        ]
      }
    }));
    toast.success(`Added ${food.name} to ${mealLabels[currentMeal]}`);
    setSearchResults([]);
    setSearchQuery('');
    setShowFavorites(false);
  };

  const removeFoodFromMeal = (foodId) => {
    const food = todayMeals[currentMeal].foods.find(f => f.id === foodId);
    const foodName = food?.name;
    setTodayMeals(prev => ({
      ...prev,
      [currentMeal]: {
        ...prev[currentMeal],
        foods: prev[currentMeal].foods.filter(f => f.id !== foodId)
      }
    }));
    if (foodName) {
      toast.info(`Removed ${foodName}`);
    }
  };

  const toggleFavorite = async (food) => {
    const isFavorite = favorites.some(f => f.name.toLowerCase() === food.name.toLowerCase());
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/api/nutrition/favorites/${encodeURIComponent(food.name)}`);
        setFavorites(prev => prev.filter(f => f.name.toLowerCase() !== food.name.toLowerCase()));
        toast.info(`Removed ${food.name} from favorites`);
      } else {
        const response = await axios.post(`${API}/api/nutrition/favorites`, {
          name: food.name,
          calories: food.calories,
          protein: food.protein,
          carbs: food.carbs,
          fat: food.fat,
          serving_size: food.serving_size || '100g',
          category: food.category || 'favorites'
        });
        if (!response.data.already_exists) {
          setFavorites(prev => [response.data.favorite, ...prev]);
          toast.success(`Added ${food.name} to favorites`);
        }
      }
    } catch (error) {
      console.error('Toggle favorite error:', error);
      toast.error('Failed to update favorites');
    }
  };

  const isFavorite = (foodName) => {
    return favorites.some(f => f.name.toLowerCase() === foodName.toLowerCase());
  };

  const logMeal = async () => {
    const meal = todayMeals[currentMeal];
    
    if (meal.foods.length === 0) {
      toast.error('Please add at least one food item');
      return;
    }

    if (!feeling.trim()) {
      toast.error('Please describe how you felt');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/api/nutrition/log-meal`, {
        meal_type: currentMeal,
        foods: meal.foods,
        feeling: feeling.trim(),
        total_calories: meal.foods.reduce((sum, f) => sum + f.calories, 0),
        total_protein: meal.foods.reduce((sum, f) => sum + f.protein, 0),
        total_carbs: meal.foods.reduce((sum, f) => sum + f.carbs, 0),
        total_fat: meal.foods.reduce((sum, f) => sum + f.fat, 0)
      });

      setTodayMeals(response.data.meals);
      toast.success(`${mealLabels[currentMeal]} logged!`);
      setFeeling('');
      
      // Refresh weekly summary after logging
      loadWeeklySummary();
      
      if (response.data.points_earned > 0) {
        const mainMeals = ['breakfast', 'lunch', 'dinner'];
        if (mainMeals.includes(currentMeal)) {
          if (response.data.points_earned === 3) {
            toast.success(`+1 point for ${mealLabels[currentMeal]}!`);
            setTimeout(() => {
              toast.success('🎉 All main meals logged! +2 bonus points!', { duration: 5000 });
            }, 1000);
          } else {
            toast.success(`+${response.data.points_earned} point${response.data.points_earned > 1 ? 's' : ''} earned!`);
          }
        }
      }
      
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        response.data.new_badges.forEach((badge, index) => {
          setTimeout(() => {
            toast.success(`🏆 Badge Earned: ${badge}!`, { duration: 5000 });
          }, 2000 + (index * 1000));
        });
      }
      
      if (response.data.level_increased) {
        setTimeout(() => {
          toast.success('🎊 Level Up! You\'re getting stronger!', { duration: 5000 });
        }, 1500);
      }
      
      const mealOrder = ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack', 'dinner', 'evening_snack'];
      const currentIndex = mealOrder.indexOf(currentMeal);
      if (currentIndex < mealOrder.length - 1) {
        setCurrentMeal(mealOrder[currentIndex + 1]);
      }
      
    } catch (error) {
      console.error('Failed to log meal:', error);
      toast.error('Failed to log meal');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Card className="bg-blue-900/70 border border-blue-700">
        <CardContent className="py-8 text-center">
          <div className="animate-pulse text-yellow-400">Loading nutrition tracker...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Weekly Summary Card */}
      {weeklySummary && weeklySummary.days_tracked > 0 && (
        <Card className="bg-gradient-to-r from-purple-900/70 to-blue-900/70 border border-purple-500/50" data-testid="nutrition-weekly-summary-card">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowWeeklySummary(!showWeeklySummary)} data-testid="nutrition-weekly-summary-toggle">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                <CardTitle className="text-white text-base">Weekly Nutrition Summary</CardTitle>
              </div>
              <span className="text-purple-300 text-sm">{showWeeklySummary ? '▲' : '▼'}</span>
            </div>
          </CardHeader>
          {showWeeklySummary && (
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-blue-800/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-yellow-400">{weeklySummary.avg_calories}</p>
                  <p className="text-xs text-gray-400">Avg Calories/Day</p>
                </div>
                <div className="bg-blue-800/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-400">{weeklySummary.avg_protein}g</p>
                  <p className="text-xs text-gray-400">Avg Protein</p>
                </div>
                <div className="bg-blue-800/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{weeklySummary.avg_carbs}g</p>
                  <p className="text-xs text-gray-400">Avg Carbs</p>
                </div>
                <div className="bg-blue-800/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400">{weeklySummary.avg_fat}g</p>
                  <p className="text-xs text-gray-400">Avg Fat</p>
                </div>
              </div>
              
              {/* Mini Chart - Daily Calories */}
              <div className="bg-blue-800/30 p-3 rounded-lg">
                <p className="text-xs text-gray-300 mb-2">Daily Calories This Week</p>
                <div className="flex items-end justify-between gap-1" style={{ height: '64px' }}>
                  {(() => {
                    const maxCal = Math.max(...weeklySummary.daily_breakdown.map(d => d.calories), 1);
                    return weeklySummary.daily_breakdown.map((day) => {
                      const heightPx = day.calories > 0 ? Math.max(Math.round((day.calories / maxCal) * 48), 4) : 2;
                      return (
                        <div key={day.date} className="flex flex-col items-center justify-end flex-1 h-full">
                          <div 
                            className={`w-full rounded-t ${day.calories > 0 ? 'bg-yellow-500' : 'bg-gray-600'}`}
                            style={{ height: `${heightPx}px` }}
                            title={`${day.calories} cal`}
                          />
                          <span className="text-[10px] text-gray-300 mt-1">{day.day}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              
              <p className="text-xs text-gray-400 mt-2 text-center">
                {weeklySummary.days_tracked} {weeklySummary.days_tracked === 1 ? 'day' : 'days'} tracked · {weeklySummary.total_meals} {weeklySummary.total_meals === 1 ? 'meal' : 'meals'} logged
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Main Nutrition Tracker Card */}
      <Card className="bg-blue-900/70 border border-blue-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg">Daily Meal Log</CardTitle>
          <p className="text-gray-300 text-sm">Track what you eat and how you feel</p>
          
          {/* Nutrition Tips */}
          <div className="mt-3 p-3 bg-green-900/50 rounded-lg border border-green-400/40">
            <h4 className="text-green-400 font-semibold mb-2 text-sm">💡 Nutrition Tips</h4>
            <div className="text-xs text-gray-300 space-y-1">
              <p>• Focus on how foods make you feel, not just what you eat</p>
              <p>• Eat within 2-3 hours of training for best performance</p>
              <p>• Stay consistent with meal timing</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Meal Tabs */}
          <div className="flex gap-2 flex-wrap">
            {Object.keys(mealLabels).map((mealKey) => (
              <Button
                key={mealKey}
                onClick={() => setCurrentMeal(mealKey)}
                data-testid={`nutrition-meal-tab-${mealKey}`}
                variant="outline"
                size="sm"
                className={`${
                  currentMeal === mealKey
                    ? 'bg-yellow-500 text-blue-900 border-yellow-500'
                    : todayMeals[mealKey].completed
                    ? 'bg-green-700 text-white border-green-600'
                    : 'bg-blue-800 text-white border-blue-600'
                }`}
              >
                {todayMeals[mealKey].completed && <Check className="w-3 h-3 mr-1" />}
                {mealLabels[mealKey].split(' ')[0]} {mealLabels[mealKey].split(' ').slice(1).join(' ')}
              </Button>
            ))}
          </div>

          {/* Current Meal Section */}
          {!todayMeals[currentMeal].completed ? (
            <div className="space-y-3">
              <h3 className="text-white font-semibold">{mealLabels[currentMeal]}</h3>
              
              {/* Food Search */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchFood()}
                    placeholder="Search foods to add..."
                    data-testid="nutrition-search-input"
                    className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  />
                  <Button
                    onClick={searchFood}
                    data-testid="nutrition-search-button"
                    disabled={searching || !searchQuery.trim()}
                    variant="outline"
                    className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-blue-900"
                  >
                    {searching ? '...' : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Favorites Quick Access */}
                {favorites.length > 0 && (
                  <Button
                    onClick={() => { setShowFavorites(!showFavorites); setSearchResults([]); }}
                    variant="ghost"
                    size="sm"
                    data-testid="nutrition-show-favorites"
                    className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 p-1 h-auto"
                  >
                    <Star className="w-3 h-3 mr-1 fill-yellow-400" />
                    <span className="text-xs">Quick add from favorites ({favorites.length})</span>
                  </Button>
                )}
              </div>

              {/* Favorites List */}
              {showFavorites && favorites.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1" data-testid="nutrition-favorites-list">
                  <p className="text-xs text-yellow-400 font-medium mb-1">⭐ Your Favorites</p>
                  {favorites.map((food) => (
                    <div key={food.id || food.name} className="bg-yellow-900/30 p-2 rounded border border-yellow-600/50 flex justify-between items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-white text-sm font-medium block truncate">{food.name}</span>
                        <span className="text-gray-400 text-xs">
                          {food.calories} cal · {food.protein}g P · {food.carbs}g C · {food.fat}g F
                        </span>
                      </div>
                      <Button
                        onClick={() => addFoodToMeal(food)}
                        size="sm"
                        data-testid={`nutrition-add-favorite-${food.id || food.name}`}
                        className="bg-green-600 hover:bg-green-700 text-white h-7 shrink-0"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="max-h-60 overflow-y-auto space-y-1" data-testid="nutrition-search-results">
                  {searchResults.map((food) => (
                    <div key={food.fdc_id || food.name} data-testid={`nutrition-search-result-${food.fdc_id || food.name}`} className="bg-blue-800/50 p-2 rounded border border-blue-600 flex justify-between items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-white text-sm font-medium block truncate" data-testid={`nutrition-search-result-name-${food.fdc_id || food.name}`}>{food.name}</span>
                        <span className="text-gray-400 text-xs">
                          {food.calories} cal · {food.protein}g P · {food.carbs}g C · {food.fat}g F
                          {food.serving_size && <span className="text-gray-500"> · {food.serving_size}</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          onClick={() => toggleFavorite(food)}
                          size="sm"
                          variant="ghost"
                          data-testid={`nutrition-favorite-${idx}`}
                          className={`h-7 w-7 p-0 ${isFavorite(food.name) ? 'text-yellow-400' : 'text-gray-500 hover:text-yellow-400'}`}
                        >
                          <Star className={`w-3 h-3 ${isFavorite(food.name) ? 'fill-yellow-400' : ''}`} />
                        </Button>
                        <Button
                          data-testid={`nutrition-add-food-${idx}`}
                          onClick={() => addFoodToMeal(food)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white h-7"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* No Results - Show Add Option */}
              {searchQuery && !searching && searchResults.length === 0 && !showFavorites && (
                <div className="bg-yellow-900/30 p-4 rounded-lg border border-yellow-500/50">
                  <p className="text-yellow-400 text-sm mb-3">
                    😕 Couldn't find "{searchQuery}" in our database
                  </p>
                  <Button
                    onClick={requestCustomFood}
                    data-testid="nutrition-add-custom-food-button"
                    className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 w-full"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add "{searchQuery}" with estimated nutrition
                  </Button>
                  <p className="text-xs text-gray-400 mt-2">
                    We'll add it with estimated values. You can log it immediately!
                  </p>
                </div>
              )}

              {/* Current Meal Foods - With Remove Button */}
              {todayMeals[currentMeal].foods.length > 0 && (
                <div className="bg-blue-800/30 p-3 rounded border border-blue-700" data-testid="nutrition-current-meal-foods">
                  <p className="text-gray-300 text-sm mb-2">Added to this meal:</p>
                  <ul className="text-white text-sm space-y-2">
                    {todayMeals[currentMeal].foods.map((food) => (
                      <li key={food.id} className="flex items-center justify-between bg-blue-900/50 p-2 rounded">
                        <div className="flex-1">
                          <span className="font-medium">{food.name}</span>
                          <span className="text-gray-400 text-xs ml-2">
                            {Math.round(food.calories)} cal
                          </span>
                        </div>
                        <Button
                          onClick={() => removeFoodFromMeal(food.id)}
                          size="sm"
                          variant="ghost"
                          data-testid={`nutrition-remove-food-${food.id}`}
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-2 border-t border-blue-700">
                    <p className="text-gray-400 text-xs">
                      Total: {Math.round(todayMeals[currentMeal].foods.reduce((s, f) => s + f.calories, 0))} cal · 
                      {Math.round(todayMeals[currentMeal].foods.reduce((s, f) => s + f.protein, 0) * 10) / 10}g P · 
                      {Math.round(todayMeals[currentMeal].foods.reduce((s, f) => s + f.carbs, 0) * 10) / 10}g C · 
                      {Math.round(todayMeals[currentMeal].foods.reduce((s, f) => s + f.fat, 0) * 10) / 10}g F
                    </p>
                  </div>
                </div>
              )}

              {/* Feeling Input */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">How did you feel after eating?</label>
                <Textarea
                  value={feeling}
                  onChange={(e) => setFeeling(e.target.value)}
                  placeholder="E.g., Energized, sluggish, satisfied, still hungry..."
                  data-testid="nutrition-feeling-input"
                  className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                  rows={2}
                />
              </div>

              {/* Log Meal Button */}
              <Button
                onClick={logMeal}
                data-testid="nutrition-log-meal-button"
                disabled={loading || todayMeals[currentMeal].foods.length === 0 || !feeling.trim()}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              >
                {loading ? 'Logging...' : `Log ${mealLabels[currentMeal]}`}
              </Button>
            </div>
          ) : (
            <div className="bg-green-900/30 p-4 rounded border border-green-700">
              <p className="text-green-300 font-medium mb-2">✓ {mealLabels[currentMeal]} Completed</p>
              <p className="text-gray-300 text-sm">
                <span className="font-medium">Foods:</span> {todayMeals[currentMeal].foods.map(f => f.name).join(', ')}
              </p>
              <p className="text-gray-300 text-sm mt-1">
                <span className="font-medium">Felt:</span> {todayMeals[currentMeal].feeling}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NutritionTracker;
